import { useState, type RefObject } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Caption1, Field, Input, MessageBar, MessageBarBody, Select, makeStyles } from '@fluentui/react-components';
import { createStaffMemberSchema, type Person, type ServicePlace } from '@vigeo/shared';
import { PopupDialog, tokens } from '@vigeo/ui';
import { PlacePicker } from '@/features/places';
import { createStaffMember, fetchCatalogOptions, updatePerson } from '@/lib/api';
import { sectionOn } from '@/lib/sections';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  pair: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))', gap: tokens.spacingHorizontalM },
});

export interface StaffDialogProps {
  /** `null` adds a new staff member; a person edits that one. */
  readonly person: Person | null;
  readonly open: boolean;
  readonly onClose: () => void;
  /** The button that opened it, when the page knows it better than the keyboard focus does (Safari on a Mac does not focus a button on click). */
  readonly opener?: RefObject<HTMLElement | null>;
}

const sameIds = (a: readonly ServicePlace[], b: readonly ServicePlace[]) => a.length === b.length && a.every((place, i) => place.placeId === b[i]?.placeId);

const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** What the Roster, the quick look and the profile say for a person with no job profile, and the picker's empty choice. */
export const NO_ROLE = 'No role set';
/** A job profile the person holds that the catalog no longer offers: kept, never dropped by an edit of something else. */
export const RETIRED_ROLE = 'Role no longer offered';

/**
 * Add or edit one staff member: name, email, phone, job title and service areas (ADR-016, #795). Service areas are
 * real places picked from a search box (ADR-018, #473). The OLD TYPED service area is never changed or removed
 * by this form: the edit sends only the places, and shows the typed text, unchanged, until places replace it.
 *
 * THE JOB TITLE (#795) is the person's job profile, `jobProfileKeys`, picked from the catalog's list. It is a
 * label here and nothing more: whether a person's compliance is tracked is decided by the `staffSource` marker
 * alone (`isComplianceTracked`), so staff added by hand still read "Not tracked yet" with a job title set, and no
 * checklist is asked for. A person holding more than one job profile is left as they are: this picks one, and
 * the profile's Role and employment card edits several.
 *
 * The add path sends the strict `createStaffMemberSchema` through `POST /api/staff`, which ADR-016 keeps to
 * name, contact and places; a job title picked there is then set by the same `PATCH` an edit sends, so it is
 * audited as an edit. The edit path is the ordinary `PATCH /api/people/{id}`, which keeps the person Active
 * and keeps the marker. One idempotency key per opening of the form, so a double click or a retry meets the
 * person it created, and a retry after the job title failed to save tries the job title again.
 */
export function StaffDialog({ person, open, onClose, opener }: StaffDialogProps) {
  const styles = useStyles();
  // A fresh form state for each opening, keyed by the parent, so nothing typed last time lingers.
  const [given, setGiven] = useState(person?.name.givenName ?? '');
  const [family, setFamily] = useState(person?.name.familyName ?? '');
  const [email, setEmail] = useState(person?.contact.email ?? '');
  const [phone, setPhone] = useState(person?.contact.phone ?? '');
  const initialPlaces = person?.assignment?.servicePlaces ?? [];
  const [places, setPlaces] = useState<readonly ServicePlace[]>(initialPlaces);
  const typedArea = person?.assignment?.serviceArea?.trim() ?? '';
  const initialRoles = person?.jobProfileKeys ?? [];
  const severalRoles = initialRoles.length > 1;
  const initialRole = severalRoles ? '' : initialRoles[0] ?? '';
  const [role, setRole] = useState(initialRole);
  const roleChanged = !severalRoles && role !== initialRole;
  // Read only while the window is open: the Roster keeps it mounted when closed, and the Table view does not read the catalog (#699).
  const options = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions, staleTime: Infinity, enabled: open });
  const known = options.data?.jobProfiles ?? [];
  const labelOf = (roleKey: string) => known.find((profile) => profile.key === roleKey)?.label ?? (options.isPending ? 'Loading…' : options.isError ? 'Current job title' : RETIRED_ROLE);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [key] = useState(() => crypto.randomUUID());
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: async () => {
      if (person === null) {
        const parsed = createStaffMemberSchema.safeParse({
          name: { givenName: given.trim(), familyName: family.trim() },
          contact: { email: email.trim(), phone: blankToNull(phone) },
          serviceArea: null,
          ...(places.length === 0 ? {} : { servicePlaces: places }),
        });
        if (!parsed.success) {
          setProblems(parsed.error.issues.map((issue) => (issue.path[0] === 'contact' && issue.path[1] === 'email' ? 'Enter a valid email address.' : issue.path[0] === 'name' ? 'Enter a first and last name.' : issue.path[1] === 'phone' ? 'A phone number needs at least 7 characters.' : issue.message)));
          return null;
        }
        const created = await createStaffMember(parsed.data, { idempotencyKey: key });
        if (role === '') return created;
        try {
          return await updatePerson(created.id, { jobProfileKeys: [role] });
        } catch {
          throw new Error('The staff member was added, but the job title was not saved. Press Add staff member again to save it.');
        }
      }
      return updatePerson(person.id, {
        name: { givenName: given.trim(), familyName: family.trim() },
        contact: { email: email.trim(), phone: blankToNull(phone) },
        // Only the places, and only when they changed. The typed service area is not sent: it stays as it is.
        ...(sameIds(places, initialPlaces) ? {} : { assignment: { servicePlaces: [...places] } }),
        // Only the job title, and only when it changed, so an edit of anything else never touches a person's job profiles.
        ...(roleChanged ? { jobProfileKeys: role === '' ? [] : [role] } : {}),
      });
    },
    onSuccess: async (saved) => {
      if (saved === null) return;
      await queryClient.invalidateQueries({ queryKey: ['people'] });
      onClose();
    },
    // An add whose job title failed has still added the person: the roster shows them while the form stays open.
    onError: () => { void queryClient.invalidateQueries({ queryKey: ['people'] }); },
  });
  /** Anything different from what the form opened with (typing a value and putting it back counts as nothing): asks "Discard what you typed?" first. */
  const typed = given !== (person?.name.givenName ?? '') || family !== (person?.name.familyName ?? '') || email !== (person?.contact.email ?? '')
    || phone !== (person?.contact.phone ?? '') || !sameIds(places, initialPlaces) || roleChanged;
  const error = save.error as (Error & { issues?: readonly string[] }) | null;

  const title = person === null ? 'Add staff member' : 'Edit staff member';
  return (
    <PopupDialog
      open={open}
      title={title}
      size="medium"
      opener={opener}
      dirty={typed}
      busy={save.isPending}
      onClose={onClose}
      primary={{ label: person === null ? 'Add staff member' : 'Save', busyLabel: 'Saving…', type: 'submit', form: 'staff-dialog-form' }}
    >
      <form
        id="staff-dialog-form"
        className={styles.form}
        onSubmit={(event) => { event.preventDefault(); setProblems([]); save.mutate(); }}
        aria-label={title}
      >
        <Caption1>{person === null ? `Add someone who already works for Vigeo.${sectionOn('hiring') ? ' No offer or candidate record is needed.' : ''}` : 'Change this person’s details.'}</Caption1>
        {error === null && problems.length === 0 ? null : (
          <MessageBar intent="error">
            <MessageBarBody>{[...problems, ...(error === null ? [] : [error.message, ...(error.issues ?? [])])].join(' ')}</MessageBarBody>
          </MessageBar>
        )}
        <div className={styles.pair}>
          <Field label="First name" required><Input value={given} onChange={(_, d) => setGiven(d.value)} /></Field>
          <Field label="Last name" required><Input value={family} onChange={(_, d) => setFamily(d.value)} /></Field>
        </div>
        <Field label="Email" required><Input type="email" value={email} onChange={(_, d) => setEmail(d.value)} /></Field>
        <Field label="Phone"><Input type="tel" value={phone} onChange={(_, d) => setPhone(d.value)} /></Field>
        {severalRoles ? (
          <Field label="Job title" hint="This person holds more than one role. Change them on their profile, under Role and employment.">
            <Caption1>{initialRoles.map(labelOf).join(' · ')}</Caption1>
          </Field>
        ) : (
          <Field label="Job title" hint={options.isError ? 'The list of job titles could not be loaded. The other details can still be saved.' : undefined}>
            <Select value={role} disabled={options.isPending} onChange={(_, d) => setRole(d.value)}>
              <option value="">{NO_ROLE}</option>
              {known.map((profile) => <option key={profile.key} value={profile.key}>{profile.label}</option>)}
              {initialRole === '' || known.some((profile) => profile.key === initialRole) ? null : <option value={initialRole}>{labelOf(initialRole)}</option>}
            </Select>
          </Field>
        )}
        <PlacePicker value={places} onChange={setPlaces} />
        {places.length === 0 && typedArea !== '' ? (
          <Caption1>Service area typed before places existed: “{typedArea}”. It stays on the record. Pick places above and they show instead.</Caption1>
        ) : null}
      </form>
    </PopupDialog>
  );
}
